/**
 * offlineManager.js
 * Maneja el caché de datos y la cola de pedidos para uso sin conexión.
 * Usa localStorage como almacenamiento (sin dependencias externas).
 */

const PREFIX = 'offl_'

function safeGet(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (e) {
    console.warn('[OfflineManager] Error al guardar en localStorage:', key, e)
    return false
  }
}

const OfflineManager = {

  isOnline() {
    return navigator.onLine
  },

  // ─────────────────────────────────────────────
  // Zonas + clientes del vendedor
  // ─────────────────────────────────────────────
  saveZones(sellerId, data) {
    return safeSet(`${PREFIX}zones_${sellerId}`, { data, ts: Date.now() })
  },
  getZones(sellerId) {
    const rec = safeGet(`${PREFIX}zones_${sellerId}`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Catálogo de productos
  // ─────────────────────────────────────────────
  saveProducts(sellerId, data) {
    return safeSet(`${PREFIX}prods_${sellerId}`, { data, ts: Date.now() })
  },
  getProducts(sellerId) {
    const rec = safeGet(`${PREFIX}prods_${sellerId}`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Precios por producto + cliente + lista
  // Se cachean automáticamente cuando se cargan online
  // ─────────────────────────────────────────────
  savePrice(productId, customerId, priceListId, priceData) {
    const key = `${PREFIX}price_${productId}_${customerId}_${priceListId}`
    return safeSet(key, { data: priceData, ts: Date.now() })
  },
  getPrice(productId, customerId, priceListId) {
    // 1. Intentar precio específico del cliente
    const key = `${PREFIX}price_${productId}_${customerId}_${priceListId}`
    const rec = safeGet(key)
    if (rec) return rec.data
    // 2. Fallback: precio base de la lista (sin acuerdo de cliente)
    const baseKey = `${PREFIX}price_${productId}_null_${priceListId}`
    const baseRec = safeGet(baseKey)
    return baseRec ? baseRec.data : null
  },

  // ─────────────────────────────────────────────
  // Listas de precios
  // ─────────────────────────────────────────────
  savePriceLists(data) {
    return safeSet(`${PREFIX}price_lists`, { data, ts: Date.now() })
  },
  getPriceLists() {
    const rec = safeGet(`${PREFIX}price_lists`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Unidades de medida
  // ─────────────────────────────────────────────
  saveUnitMeasurements(data) {
    return safeSet(`${PREFIX}unit_meas`, { data, ts: Date.now() })
  },
  getUnitMeasurements() {
    const rec = safeGet(`${PREFIX}unit_meas`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Puntos de venta
  // ─────────────────────────────────────────────
  savePointsSales(data) {
    return safeSet(`${PREFIX}points_sales`, { data, ts: Date.now() })
  },
  getPointsSales() {
    const rec = safeGet(`${PREFIX}points_sales`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Medios de pago
  // ─────────────────────────────────────────────
  saveMethodsPayments(data) {
    return safeSet(`${PREFIX}methods_pay`, { data, ts: Date.now() })
  },
  getMethodsPayments() {
    const rec = safeGet(`${PREFIX}methods_pay`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Configuración del módulo
  // ─────────────────────────────────────────────
  saveConfigurations(data) {
    return safeSet(`${PREFIX}configs`, { data, ts: Date.now() })
  },
  getConfigurations() {
    const rec = safeGet(`${PREFIX}configs`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Timestamp del último sync
  // ─────────────────────────────────────────────
  setLastSync(sellerId) {
    return safeSet(`${PREFIX}last_sync_${sellerId}`, Date.now())
  },
  getLastSync(sellerId) {
    return safeGet(`${PREFIX}last_sync_${sellerId}`)
  },
  formatLastSync(sellerId) {
    const ts = this.getLastSync(sellerId)
    if (!ts) return null
    const d = new Date(ts)
    return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  },

  // ─────────────────────────────────────────────
  // Cola de pedidos offline
  // ─────────────────────────────────────────────
  getQueue() {
    return safeGet(`${PREFIX}queue`) || []
  },

  addToQueue(orderData, isEdit = false) {
    const queue = this.getQueue()
    const localId = `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
    queue.push({
      localId,
      savedAt: new Date().toISOString(),
      status: 'pending',   // pending | syncing | error | synced
      error: null,
      isEdit,
      data: orderData,
    })
    safeSet(`${PREFIX}queue`, queue)
    return localId
  },

  updateQueueItem(localId, updates) {
    const queue = this.getQueue()
    const idx = queue.findIndex(i => i.localId === localId)
    if (idx !== -1) {
      queue[idx] = { ...queue[idx], ...updates }
      safeSet(`${PREFIX}queue`, queue)
    }
  },

  removeFromQueue(localId) {
    const queue = this.getQueue().filter(i => i.localId !== localId)
    safeSet(`${PREFIX}queue`, queue)
  },

  pendingCount() {
    return this.getQueue().filter(i => i.status === 'pending' || i.status === 'error').length
  },

  // ─────────────────────────────────────────────
  // Limpieza de datos cacheados (preserva la cola de pedidos)
  // ─────────────────────────────────────────────
  clearCachedData(sellerId) {
    const keysToDelete = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(PREFIX)) continue
      // Preservar la cola de pedidos pendientes
      if (key === `${PREFIX}queue`) continue
      keysToDelete.push(key)
    }
    keysToDelete.forEach(k => localStorage.removeItem(k))
  },

  // Limpia items de la cola que ya fueron sincronizados o fallaron hace más de 7 días
  purgeQueue() {
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000
    const now = Date.now()
    const queue = this.getQueue().filter(i => {
      if (i.status === 'synced') return false
      if (i.status === 'error' && i.savedAt && (now - new Date(i.savedAt).getTime()) > SEVEN_DAYS) return false
      return true
    })
    safeSet(`${PREFIX}queue`, queue)
  },
}

export default OfflineManager
